import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGroups } from '../../hooks/useGroups';
import { useMaintenanceTypes } from '../../hooks/useMaintenanceTypes';
import {
  useRegisterMutation,
  useUpdateMutation,
  useApproveMutation,
} from '../../app/services/maintenances';
import { useAuth } from '../../hooks/useAuth';
import { fetchMaintenance } from '../../features/maintenances/maintenancesSlice';
import { fetchMaintenanceTypes } from '../../features/maintenanceTypes/maintenanceTypesSlice';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import ModalComponent from '../ModalComponent';
import ModalEditor from '../ModalEditor';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { getIntervals, getIntervalsNotify, daysPerInterval } from '../../constants/options';
import { ACTIONS } from '../../constants/actionTables';

import {
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ModalButton, ModalDialogActions } from '../ModalComponents';
import styles from '../ModalComponent/modalStyles.module.css';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postMaintenanceTaskSchema } from '../../schemas/postMaintenanceTaskSchema';

const initialForm = {
  id: '',
  name: '',
  interval: { id: '' },
  notificationLeadTime: '',
  maintenanceType: { id: '' },
  machineGroup: '',
  externalServiceAddress: '',
};

export default function ModalAddMaintanenceTask({ id, type, fetchData, groupId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [types, setTypes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [content, setContent] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [planMeta, setPlanMeta] = useState({ approval_status: null, created_by: null });
  const [approveMaintenance] = useApproveMutation();
  const { user: currentUser } = useAuth();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const nameError = getValidationErrorMessage(formValidationErrors, 'name');
  const descrError = getValidationErrorMessage(formValidationErrors, 'description');
  const intervalError = getValidationErrorMessage(formValidationErrors, 'interval');
  const notificationLeadTimeError = getValidationErrorMessage(
    formValidationErrors,
    'notificationLeadTime'
  );
  const maintenanceTypeError = getValidationErrorMessage(formValidationErrors, 'maintenanceType');

  const { t } = useTranslation();
  const intervals = getIntervals(t);
  const dispatch = useDispatch();
  const { groups } = useGroups();
  const [registerMaintenance, { error }] = useRegisterMutation();
  const { maintenanceTypes } = useMaintenanceTypes();
  const [updateMaintenance] = useUpdateMutation();

  const computedLeadTimeOptions = useMemo(() => {
    const leadTimeOptions = getIntervalsNotify(t);
    const interval = form.interval;
    if (!interval) {
      return leadTimeOptions;
    }

    const days = daysPerInterval[interval.id];
    if (!days) {
      return leadTimeOptions;
    }

    return leadTimeOptions.filter((leadTimeOption) => leadTimeOption.value <= days);
  }, [t, form.interval]);

  useEffect(() => {
    if (id && open) {
      async function fetchData() {
        try {
          const response = await dispatch(fetchMaintenance(id));
          const intervalResponse = JSON.parse(response.payload.interval);
          const fetchedInterval = intervals.find(
            (interval) =>
              interval.value.type === intervalResponse.type &&
              interval.value.value === intervalResponse.value
          );
          setForm({
            id: response.payload.id,
            name: response.payload.name,
            interval: fetchedInterval,
            notificationLeadTime: response.payload.notification_lead_time,
            maintenanceType: response.payload.maintenance_type,
            machineGroup: response.payload.machine_group,
            externalServiceAddress: response.payload.external_service_address || '',
          });
          setPlanMeta({
            approval_status: response.payload.approval_status,
            created_by: response.payload.created_by,
          });
          let description;

          if (typeof response.payload.description === 'string') {
            try {
              description = JSON.parse(response.payload.description);
            } catch (error) {
              // If parsing fails, treat as plain text and create empty Draft.js content
              console.warn('Description is not valid JSON, using empty content');
              description = { blocks: [], entityMap: {} };
            }
          } else {
            description = response.payload.description;
          }
          setContent(description);
        } catch (error) {
          console.error('Error fetching maintenance:', error);
        }
      }
      fetchData();
    }
  }, [id, open, dispatch]); // Removed intervals from dependencies

  useEffect(() => {
    if (error) {
      setErrorResponse(error.data.message);
    }
  }, [error]);

  useEffect(() => {
    if (maintenanceTypes) {
      setTypes(maintenanceTypes);
    }
  }, [groups, maintenanceTypes]);

  useEffect(() => {
    if (types) {
      setLoading(false);
    }
  }, [types]);

  // Fetch maintenance types when modal opens - only once
  useEffect(() => {
    if (open && (!maintenanceTypes || maintenanceTypes.length === 0)) {
      dispatch(fetchMaintenanceTypes());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isViewMode = type === 'view';
  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty && !isViewMode, open && !isViewMode);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setContent(null);
    setFormValidationErrors(null);
    setIsSubmitting(false);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    const { name, value } = e.target;
    const numericValue = parseInt(value, 10);

    // Clear validation error for the field being changed
    if (formValidationErrors && formValidationErrors[name]) {
      setFormValidationErrors({
        ...formValidationErrors,
        [name]: null,
      });
    }

    switch (name) {
      case 'maintenanceType':
        setForm({
          ...form,
          maintenanceType: {
            id: numericValue,
          },
        });
        break;
      case 'interval':
        setForm({
          ...form,
          notificationLeadTime: '',
          interval: { id: numericValue },
        });
        break;
      case 'notificationLeadTime':
        setForm({
          ...form,
          notificationLeadTime: numericValue,
        });
        break;
      default:
        setForm({ ...form, [name]: value });
    }
  };

  const validateBeforeSummary = () => {
    if (!form.maintenanceType?.id) {
      setFormValidationErrors({
        maintenanceType: t('validation.required'),
      });
      return false;
    }

    const validationErrors = validateFormData(t, postMaintenanceTaskSchema, {
      ...form,
      description: content,
    });
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return false;
    }
    return true;
  };

  const handleSubmitRequest = () => {
    if (isSubmitting) return;
    if (!validateBeforeSummary()) return;
    setShowSummary(true);
  };

  const handleApprove = async () => {
    try {
      await approveMaintenance(id).unwrap();
      await fetchData();
      doClose();
    } catch (err) {
      const key = err?.data?.message;
      const known = key && t(`approval.${key}`, { defaultValue: '' });
      setErrorResponse(known || key || t('errors.generic'));
    }
  };

  const handleSubmit = async () => {
    setShowSummary(false);

    setIsSubmitting(true);
    setErrorResponse('');

    const selectedTypeIsExternal = !!types?.find(
      (tp) => +tp.id === +form.maintenanceType?.id,
    )?.is_external_service;

    if (id) {
      try {
        const formWithDescription = { ...form };
        formWithDescription.description = JSON.stringify(content);
        const selectedInterval = intervals.find((interval) => interval.id === form.interval.id);
        formWithDescription.interval = JSON.stringify(selectedInterval.value);
        if (!selectedTypeIsExternal) {
          formWithDescription.externalServiceAddress = '';
        }
        const result = await updateMaintenance({ id, form: formWithDescription });

        if (result.error) {
          const errorMessage =
            result.error?.data?.message ||
            result.error?.data?.sqlMessage ||
            t('modals.errorUpdating');
          setErrorResponse(errorMessage);
          console.error('Error update task:', result.error);
        } else {
          await fetchData();
          doClose();
        }
      } catch (err) {
        const errorMessage =
          err?.data?.message || err?.data?.sqlMessage || err.message || t('modals.errorUpdating');
        setErrorResponse(errorMessage);
        console.error('Error update task:', err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        const formWithoutId = { ...form };
        delete formWithoutId.id;
        formWithoutId.description = content;
        formWithoutId.machineGroup = { id: +groupId };
        const selectedInterval = intervals.find((interval) => interval.id === form.interval.id);

        if (!selectedInterval) {
          setErrorResponse('Invalid interval selected');
          return;
        }

        formWithoutId.interval = JSON.stringify(selectedInterval.value);
        if (!selectedTypeIsExternal) {
          formWithoutId.externalServiceAddress = '';
        }

        const result = await registerMaintenance(formWithoutId);

        if (result.error) {
          const errorMessage =
            result.error?.data?.message ||
            result.error?.data?.sqlMessage ||
            t('modals.errorCreating');
          setErrorResponse(errorMessage);
          console.error('Error registering task:', result.error);

          // If it's a foreign key error related to maintenance type
          if (result.error?.data?.code === 'ER_NO_REFERENCED_ROW_2') {
            setFormValidationErrors({
              ...formValidationErrors,
              maintenanceType:
                t('validation.invalidMaintenanceType') || 'Please select a valid maintenance type',
            });
          }
        } else {
          await fetchData();
          doClose();
        }
      } catch (err) {
        const errorMessage =
          err?.data?.message || err?.data?.sqlMessage || err.message || t('modals.errorCreating');
        setErrorResponse(errorMessage);
        console.error('Error registering task:', err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div>
      {type === 'add' ? (
        <ModalButton
          variant="contained"
          color="primary"
          onClick={handleOpen}
          style={{ padding: '12px 24px', textTransform: 'none' }}
          startIcon={<AddIcon />}
        >
          {t('modals.add')}
        </ModalButton>
      ) : type === 'view' ? (
        <Tooltip title={t('generic.view')} placement="top">
          <IconButton color="primary" onClick={handleOpen} style={iconButtonStyles}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ) : type === 'edit' ? (
        <Tooltip title={t('generic.edit')} placement="top">
          <IconButton color="primary" onClick={handleOpen} style={iconButtonStyles}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <ModalButton
          variant="contained"
          color="primary"
          style={{ padding: '5px 15px' }}
          onClick={handleOpen}
        >
          {t('generic.edit')}
        </ModalButton>
      )}

      <ModalComponent open={open} handleClose={handleClose}>
        {!loading ? (
          <div className={styles.modal}>
            <DialogTitle sx={{ m: 0, p: 2 }}>
              {type === 'add' ? t('modals.create') : t('modals.update')}{' '}
              {t('modals.maintenanceTask')}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={(theme) => ({
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ p: 3 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <TextField
                  value={form.name}
                  label={t('generic.name')}
                  variant="outlined"
                  name="name"
                  onChange={handleChange}
                  fullWidth
                  error={!!nameError}
                  helperText={nameError ?? ' '}
                  sx={commonTextFieldStyles}
                />

                <FormControl>
                  <ModalEditor
                    content={content}
                    setContent={(c) => { setIsDirty(true); setContent(c); }}
                    type={ACTIONS.EDIT}
                  />
                  <FormHelperText error>{descrError ?? ' '}</FormHelperText>
                </FormControl>

                <TextField
                  value={form.interval?.id || ''}
                  select
                  label={t('generic.interval')}
                  name="interval"
                  onChange={handleChange}
                  fullWidth
                  error={!!intervalError}
                  helperText={intervalError ?? ' '}
                  sx={commonTextFieldStyles}
                >
                  {intervals.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.period}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  value={form.notificationLeadTime || ''}
                  select
                  label={t('specificGroup.NotificationLeadTime')}
                  name="notificationLeadTime"
                  onChange={handleChange}
                  fullWidth
                  error={!!notificationLeadTimeError}
                  helperText={notificationLeadTimeError ?? ' '}
                  sx={commonTextFieldStyles}
                >
                  {computedLeadTimeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.period}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  value={form.maintenanceType.id || ''}
                  select
                  label={t('generic.maintenanceType')}
                  name="maintenanceType"
                  onChange={handleChange}
                  fullWidth
                  error={!!maintenanceTypeError}
                  helperText={maintenanceTypeError ?? ' '}
                  sx={commonTextFieldStyles}
                >
                  {types &&
                    types.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                </TextField>

                {!!types?.find(
                  (tp) => +tp.id === +form.maintenanceType?.id,
                )?.is_external_service && (
                  <TextField
                    value={form.externalServiceAddress || ''}
                    label={t('generic.externalServiceAddress')}
                    name="externalServiceAddress"
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t('generic.externalServiceAddressPlaceholder')}
                    sx={commonTextFieldStyles}
                  />
                )}

                {errorResponse && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {Array.isArray(errorResponse) ? (
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {errorResponse.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      errorResponse
                    )}
                  </Alert>
                )}
              </div>
            </DialogContent>
            <ModalDialogActions>
              <ModalButton
                variant="outlined"
                color="primary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('modals.cancel')}
              </ModalButton>
              {(() => {
                if (type === 'view') return null;
                const isCreate = !id;
                if (isCreate || isDirty) {
                  return (
                    <ModalButton
                      variant="contained"
                      color="primary"
                      onClick={handleSubmitRequest}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t('generic.loading') || 'Loading...'
                        : t('approval.submitForApproval')}
                    </ModalButton>
                  );
                }
                const isPending = planMeta.approval_status === 'pending_approval';
                const isCreator = currentUser && +currentUser.id === +planMeta.created_by;
                if (isPending && !isCreator) {
                  return (
                    <ModalButton
                      variant="contained"
                      onClick={handleApprove}
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: '#ed6c02',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#c75800' },
                      }}
                    >
                      {t('approval.approve')}
                    </ModalButton>
                  );
                }
                return null;
              })()}
            </ModalDialogActions>
          </div>
        ) : null}
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />

      <Dialog open={showSummary} onClose={() => setShowSummary(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('approval.summaryBeforeSave')}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ '& > div': { mb: 1 } }}>
            <div>
              <strong>{t('generic.name')}:</strong> {form.name || '-'}
            </div>
            <div>
              <strong>{t('generic.maintenanceType')}:</strong>{' '}
              {types?.find((tp) => +tp.id === +form.maintenanceType?.id)?.name || '-'}
            </div>
            {!!types?.find(
              (tp) => +tp.id === +form.maintenanceType?.id,
            )?.is_external_service && (
              <div>
                <strong>{t('generic.externalServiceAddress')}:</strong>{' '}
                {form.externalServiceAddress?.trim() || '-'}
              </div>
            )}
            <div>
              <strong>{t('generic.machineGroup')}:</strong>{' '}
              {form.machineGroup?.name ||
                (groups?.activeMachineGroups || []).find((g) => +g.id === +groupId)?.name ||
                '-'}
            </div>
            <div>
              <strong>{t('generic.interval')}:</strong>{' '}
              {intervals.find((iv) => iv.id === form.interval?.id)?.period || '-'}
            </div>
            <div>
              <strong>{t('specificGroup.NotificationLeadTime')}:</strong>{' '}
              {computedLeadTimeOptions.find((o) => o.value === form.notificationLeadTime)?.period || '-'}
            </div>
            <div>
              <strong>{t('generic.description')}:</strong>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                {(content?.blocks || []).map((b) => b.text).join('\n').trim() || '-'}
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ModalButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('generic.loading') || 'Loading...' : t('approval.submitForApproval')}
          </ModalButton>
          <ModalButton
            variant="outlined"
            onClick={() => setShowSummary(false)}
            disabled={isSubmitting}
          >
            {t('modals.cancel')}
          </ModalButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
